<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { onBeforeMount, provide, ref, shallowReactive, watch } from 'vue';

import type { BookingWidgetContext } from '@/types/booking-widget-context';
import type { ProductDTO } from '@/types/products';

import { useBookingWidget } from '@/composables/booking/useBookingWidget';
import { createCartsTransactionsApi } from '@/services/booking/cartsTransactionsApi.ts';
import { useDetailsStore } from '@/stores/useDetailsStore.ts';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore.ts';
import { BookingWidgetContextKey } from '@/types/booking-widget-context';

import AvailabilityCheckButton from './AvailabilityCheckButton.vue';
import BookingOptionsDropdown from './BookingOptionsDropdown.vue';
import DateSelectionDropdown from './DateSelectionDropdown.vue';
import SessionsView from './SessionsView.vue';
import UnitsDropdown from './UnitsDropdown.vue';

type NextSession = {
  availabilityType: 'STARTING_TIME' | 'OPENING_HOURS';
  date: number;
  duration: number;
  productId: string;
};

const props = defineProps<{
  productId?: string;
  activityDetails?: ProductDTO;
  nextSession?: NextSession;
}>();

const configurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(configurationStore);

const detailsStore = useDetailsStore();
const { fetchDistributor, fetchActivityDetails } = detailsStore;
const { productDetails } = storeToRefs(detailsStore);

const activityDetails = ref<ProductDTO | undefined>(props.activityDetails);
const nextSession = ref<NextSession | null | undefined>(props.nextSession);
const cartsTransactionsApi = createCartsTransactionsApi();

const bookingWidgetState = useBookingWidget(
  cartsTransactionsApi,
  globalConfiguration.value?.apikey ?? '',
  activityDetails,
);

const { selectedOption, bookingType } = bookingWidgetState;

const bookingWidgetContext = shallowReactive<BookingWidgetContext>({
  bookingWidget: bookingWidgetState,
  cartsTransactionsApi,
  activityDetails: props.activityDetails,
  nextSession: props.nextSession,
});

provide(BookingWidgetContextKey, bookingWidgetContext);

watch(activityDetails, (details) => {
  if (details && details.options.length === 0) {
    console.warn(`[BookingWidget] Product "${details.id}" has no booking options available`);
  }
  if (details && details.options.length === 1) {
    bookingWidgetState.handleOptionSelected(details.options[0]);
  }
}, { immediate: true });

async function loadActivity() {
  if (!props.productId)
    return;

  try {
    await fetchDistributor();
    await fetchActivityDetails(props.productId);

    const loaded = productDetails.value;
    activityDetails.value = loaded;
    bookingWidgetContext.activityDetails = loaded;

    const loadedNextSession = loaded?.properties.find(
      property => property.propertyName === 'nextSessionDate',
    )?.value ?? null;
    nextSession.value = loadedNextSession;
    bookingWidgetContext.nextSession = loadedNextSession ?? undefined;
  }
  catch (err) {
    console.error('Failed to load activity details', err);
  }
}

onBeforeMount(async () => {
  if (props.activityDetails) {
    // Embedded mode: data already provided by parent (e.g. Details.vue)
    return;
  }

  await loadActivity();
});
</script>

<template>
  <div v-if="activityDetails" class="booking-widget yb-preset">
    <p v-if="activityDetails.options.length === 0" class="no-options-message">
      {{ $t('booking.noOptionsAvailable') }}
    </p>

    <template v-else>
      <BookingOptionsDropdown v-if="activityDetails.options.length > 1" />

      <!-- MVP: Gift mode disabled for now -->
      <!-- <PurchaseGiftToggle /> -->

      <div v-if="selectedOption && bookingType === 'purchase'" class="booking-selection-row">
        <UnitsDropdown />
        <DateSelectionDropdown />
      </div>

      <AvailabilityCheckButton
        v-if="selectedOption && bookingType === 'purchase' && !bookingWidgetState.hasCheckedOnce.value"
      />

      <SessionsView
        v-if="selectedOption && bookingType === 'purchase' && bookingWidgetState.hasCheckedOnce.value"
      />
    </template>
  </div>
</template>

<style scoped lang="css">
.booking-widget {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.booking-selection-row {
  display: flex;
}

.no-options-message {
  margin: 0;
  padding: var(--yb-spacing-base);
  text-align: center;
  color: var(--yb-color-darkgray);
  font-size: var(--yb-text-sm);
}
</style>
